import React, { useState, useEffect } from 'react'

import LoadingPlaceholder from 'components/LoadingPlaceholder'
import { Flex } from 'components/Box'
import InfiniteList, { handleFetchMore } from 'components/InfiniteList'
import EmptyState from 'modules/shares/components/EmptyState'

import { Layout } from '../components/Layout'
import {
  ListGrid,
  ListGridItem,
  ItemImage,
  GroupName,
  ItemName,
  Group,
  ItemImageWrapper,
} from '../components/Grid'

import { useGetSymbols } from '../operations'
import { groupStyles } from '../utils'
import useResponsiveImage from '../useResponsiveImage'
import { useSearchComponentsContext } from '../SearchCwvContext'

import type { Thumbnail, File } from '../types'

interface SymbolCWV {
  name: string
  identifier: string
  artboard: { files: { url: string; thumbnails: Thumbnail[] }[] }
}

const SymbolItem = ({ symbol }: { symbol: SymbolCWV }) => {
  const imageProps = useResponsiveImage({
    name: symbol.name,
    files: symbol.artboard.files as File[],
  })

  return (
    <ListGridItem>
      <ItemImageWrapper>
        <ItemImage {...imageProps} />
      </ItemImageWrapper>
      <ItemName>{symbol.name}</ItemName>
    </ListGridItem>
  )
}

const SymbolsList = ({
  searchCwv,
  path,
}: {
  searchCwv: string
  path: string | null
}) => {
  const { entries, meta, loading, filter, fetchMore } = useGetSymbols(searchCwv)

  useEffect(() => {
    filter(path === 'View All' ? null : path)
    // we do NOT want to add filter as a dependency here
    // eslint-disable-next-line
  }, [path])

  if (loading) {
    return (
      <Flex justifyContent="center" flex="auto">
        <LoadingPlaceholder size="64px" />
      </Flex>
    )
  }

  const symbolsGroups = groupStyles(entries)
  const groupsNames = Object.keys(symbolsGroups)

  if (groupsNames.length === 0) {
    if (searchCwv !== '') {
      return (
        <EmptyState
          title="No search results"
          description="No matching items."
          icon="search"
        />
      )
    }

    return (
      <EmptyState
        title="No symbols"
        description="This Document does not contain any Symbols."
        icon="symbol"
      />
    )
  }

  return (
    <InfiniteList
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'version', 'document', 'components', 'entries'],
        { after: meta?.after || null }
      )}>
      {groupsNames.map(groupName => (
        <Group key={groupName}>
          <GroupName groups={groupName} />
          <ListGrid>
            {symbolsGroups[groupName].map((symbol: SymbolCWV) => (
              <SymbolItem symbol={symbol} key={symbol.identifier} />
            ))}
          </ListGrid>
        </Group>
      ))}
    </InfiniteList>
  )
}

export const Symbols: React.FC = () => {
  const { searchCwv } = useSearchComponentsContext()
  const [selected, setSelected] = useState('View All')

  return (
    <Layout onSelect={setSelected} type="SYMBOL" selected={selected}>
      <SymbolsList searchCwv={searchCwv} path={selected} />
    </Layout>
  )
}
